import type { DAGData, DAGEdge, DAGNode } from "../../api/artifacts";
import type {
  CanonicalActorRecord,
  CanonicalArtifactRecord,
  CanonicalArtifactVersionRecord,
  CanonicalBindingRecord,
  CanonicalBindingStateEventRecord,
  CanonicalDecisionEvidenceRecord,
  CanonicalDecisionRecord,
  CanonicalDecisionStatusEventRecord,
  CanonicalJournalEntryRecord,
  CanonicalLifecycleEventRecord,
  CanonicalLineageBoundary,
  CanonicalLineageEntityKind,
  CanonicalLineageProjectionBucket,
  CanonicalLineageProjectionEnvelope,
  CanonicalLineageReadModel,
  CanonicalLineageRecordRef,
  CanonicalMergeInputRecord,
  CanonicalOperationRunRecord,
  CanonicalOperationSpecRecord,
  CanonicalOperationStateEventRecord,
  CanonicalSupersessionRecord,
  CanonicalToolSnapshotRecord,
  CanonicalTopologyRetirementRecord,
  CanonicalUnknownRecord,
} from "../../api/canonicalLineage";
import type { DagRecordType } from "./types";

type CanonicalRecord =
  | CanonicalArtifactRecord
  | CanonicalArtifactVersionRecord
  | CanonicalOperationSpecRecord
  | CanonicalToolSnapshotRecord
  | CanonicalActorRecord
  | CanonicalOperationRunRecord
  | CanonicalBindingRecord
  | CanonicalBindingStateEventRecord
  | CanonicalOperationStateEventRecord
  | CanonicalSupersessionRecord
  | CanonicalMergeInputRecord
  | CanonicalTopologyRetirementRecord
  | CanonicalLifecycleEventRecord
  | CanonicalDecisionRecord
  | CanonicalDecisionEvidenceRecord
  | CanonicalDecisionStatusEventRecord
  | CanonicalJournalEntryRecord
  | CanonicalUnknownRecord;

type CanonicalCollectionName = Exclude<
  keyof CanonicalLineageReadModel,
  "contract_version" | "context" | "semantic_signals" | "warnings" | "unknown_relationships"
>;

const RECORD_COLLECTIONS = [
  "artifacts",
  "artifact_versions",
  "operation_specs",
  "tool_snapshots",
  "actors",
  "operation_runs",
  "bindings",
  "binding_state_events",
  "operation_state_events",
  "supersessions",
  "merge_inputs",
  "topology_retirements",
  "lifecycle_events",
  "decisions",
  "decision_evidence",
  "decision_status_events",
  "journal_entries",
  "unknown_records",
] as const satisfies readonly CanonicalCollectionName[];

export class CanonicalLineagePageConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CanonicalLineagePageConflictError";
  }
}

export interface MergedCanonicalLineagePages {
  /** Exact, strictly validated server pages. These remain the source authority. */
  sourcePages: readonly CanonicalLineageProjectionEnvelope[];
  /** A deterministic union used only to build the presentation projection. */
  model: CanonicalLineageReadModel;
  buckets: CanonicalLineageProjectionBucket[];
  /** Authoritative outer roots: caller seeds for rooted modes, page roots otherwise. */
  roots: CanonicalLineageProjectionEnvelope["data"]["roots"];
  projection: CanonicalLineageProjectionEnvelope["data"]["projection"];
  effectiveAsOf: string;
  recordedAsOf: string;
  snapshotToken: string;
  nextCursor: string | null;
  truncated: boolean;
  terminalTruncation: boolean;
  loadedRecordCount: number;
  representedRecordCount: number;
}

export function canonicalLineageRefKey(ref: CanonicalLineageRecordRef): string {
  return `${ref.entity_kind}:${ref.id}`;
}

export function parseCanonicalLineageRefKey(value: string | null | undefined): CanonicalLineageRecordRef | null {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator < 1 || separator === value.length - 1) return null;
  const entityKind = value.slice(0, separator) as CanonicalLineageEntityKind;
  const id = value.slice(separator + 1);
  const knownKinds = new Set<CanonicalLineageEntityKind>([
    "artifact", "artifact_version", "operation_spec", "operation_run", "tool", "actor",
    "binding", "binding_state_event", "operation_state_event", "supersession", "merge_input",
    "topology_retirement", "lifecycle_event", "decision", "decision_evidence", "decision_status_event", "journal_entry", "unknown",
  ]);
  return knownKinds.has(entityKind) && id ? { entity_kind: entityKind, id } : null;
}

function recordIdentity(record: CanonicalRecord): string {
  if (record.entity_kind === "decision_evidence") {
    return `${record.decision_id}:${record.ordinal}`;
  }
  return record.id;
}

function stableValue(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableValue).join(",")}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableValue(entry)}`)
    .join(",")}}`;
}

function mergeUnique<T>(
  values: readonly T[],
  keyFor: (value: T) => string,
  label: string,
  preserveFirstSeenOrder = false,
): T[] {
  const result = new Map<string, { value: T; fingerprint: string }>();
  for (const value of values) {
    const key = keyFor(value);
    const fingerprint = stableValue(value);
    const previous = result.get(key);
    if (previous && previous.fingerprint !== fingerprint) {
      throw new CanonicalLineagePageConflictError(`Conflicting ${label} record ${key} across lineage pages`);
    }
    if (!previous) result.set(key, { value, fingerprint });
  }
  const entries = [...result.entries()];
  if (!preserveFirstSeenOrder) entries.sort(([left], [right]) => left.localeCompare(right));
  return entries
    .map(([, entry]) => entry.value);
}

export function mergeCanonicalLineagePages(pages: readonly CanonicalLineageProjectionEnvelope[]): MergedCanonicalLineagePages | null {
  if (!pages.length) return null;
  const first = pages[0]!;
  const rootedProjection = first.data.projection === "focus"
    || first.data.projection === "neighbourhood"
    || first.data.projection === "trace";
  const callerRootsFingerprint = stableValue(first.data.roots);
  for (const page of pages.slice(1)) {
    if (
      page.data.lineage.context.project_id !== first.data.lineage.context.project_id
      || page.data.projection !== first.data.projection
      || page.data.effective_as_of !== first.data.effective_as_of
      || page.data.recorded_as_of !== first.data.recorded_as_of
      || page.data.snapshot_token !== first.data.snapshot_token
      || page.data.contract_version !== first.data.contract_version
      || (rootedProjection && stableValue(page.data.roots) !== callerRootsFingerprint)
    ) {
      throw new CanonicalLineagePageConflictError("Lineage cursor returned a page from a different projection");
    }
  }

  const mergedCollections = Object.fromEntries(RECORD_COLLECTIONS.map((collection) => {
    const records: CanonicalRecord[] = [];
    for (const page of pages) records.push(...page.data.lineage[collection] as CanonicalRecord[]);
    return [collection, mergeUnique(records, recordIdentity, collection)];
  })) as Pick<CanonicalLineageReadModel, CanonicalCollectionName>;
  const unknownRelationships = mergeUnique(
    pages.flatMap((page) => page.data.lineage.unknown_relationships),
    (relationship) => relationship.id,
    "unknown_relationships",
  );
  const semanticSignals = mergeUnique(
    pages.flatMap((page) => page.data.lineage.semantic_signals),
    (signal) => stableValue(signal),
    "semantic_signals",
  );

  const included = new Set<string>();
  for (const collection of RECORD_COLLECTIONS) {
    for (const record of mergedCollections[collection] as CanonicalRecord[]) {
      included.add(canonicalLineageRefKey({ entity_kind: record.entity_kind, id: recordIdentity(record) }));
    }
  }
  const boundaries = mergeUnique(
    pages.flatMap((page) => page.data.lineage.context.boundary),
    canonicalLineageRefKey,
    "boundary",
  ).filter((boundary) => !included.has(canonicalLineageRefKey(boundary)));
  const buckets = mergeUnique(
    pages.flatMap((page) => page.data.buckets),
    (bucket) => `${bucket.dimension}:${bucket.key}`,
    "projection bucket",
  );
  const roots = mergeUnique(
    pages.flatMap((page) => page.data.roots),
    canonicalLineageRefKey,
    "projection root",
    true,
  );
  const last = pages[pages.length - 1]!;
  const nextCursor = last.data.next_cursor ?? null;
  const hasMore = nextCursor !== null;
  const hardTruncated = pages.some((page) => page.data.lineage.context.truncated)
    || (!hasMore && last.data.truncated);
  const truncated = hasMore || hardTruncated;
  const warnings = [...new Set(pages.flatMap((page) => [
    ...page.data.lineage.warnings,
    ...page.data.warnings,
    ...page.warnings,
  ]))].filter((warning) => hasMore || warning !== "lineage_projection_page_truncated");
  const loadedRecordCount = RECORD_COLLECTIONS.reduce(
    (count, collection) => count + mergedCollections[collection].length,
    0,
  );

  return {
    sourcePages: pages,
    model: {
      contract_version: first.data.lineage.contract_version,
      context: {
        ...last.data.lineage.context,
        root_ids: [...new Set(pages.flatMap((page) => page.data.lineage.context.root_ids))],
        boundary: boundaries,
        truncated: hardTruncated,
      },
      ...mergedCollections,
      unknown_relationships: unknownRelationships,
      semantic_signals: semanticSignals,
      warnings,
    },
    buckets,
    roots,
    projection: first.data.projection,
    effectiveAsOf: first.data.effective_as_of,
    recordedAsOf: first.data.recorded_as_of,
    snapshotToken: first.data.snapshot_token,
    nextCursor,
    truncated,
    terminalTruncation: hardTruncated && !hasMore,
    loadedRecordCount,
    representedRecordCount: buckets.length
      ? buckets.reduce((count, bucket) => count + bucket.count, 0)
      : loadedRecordCount,
  };
}

function evidenceIdentity(record: CanonicalDecisionEvidenceRecord): string {
  return `${record.decision_id}:${record.ordinal}`;
}

function ref(record: CanonicalRecord): CanonicalLineageRecordRef {
  return {
    entity_kind: record.entity_kind,
    id: record.entity_kind === "decision_evidence" ? evidenceIdentity(record) : record.id,
  };
}

function nodeType(record: CanonicalRecord): DagRecordType {
  switch (record.entity_kind) {
    case "artifact": return "artifact";
    case "artifact_version": return "version";
    case "operation_spec":
    case "operation_run":
    case "tool": return "operation";
    case "actor":
    case "journal_entry": return "human";
    case "decision":
    case "decision_evidence":
    case "decision_status_event": return "decision";
    default: return "lifecycle";
  }
}

function nodeLabel(record: CanonicalRecord, model: CanonicalLineageReadModel): string {
  const artifacts = new Map(model.artifacts.map((artifact) => [artifact.id, artifact]));
  const specs = new Map(model.operation_specs.map((spec) => [spec.id, spec]));
  switch (record.entity_kind) {
    case "artifact": return record.name;
    case "artifact_version": {
      const artifact = artifacts.get(record.artifact_id);
      return `${artifact?.name ?? "Artifact version"} · ${record.content_identity.digest.slice(0, 8)}`;
    }
    case "operation_spec": return `${record.name} · v${record.version}`;
    case "tool": return `${record.name} · ${record.version}`;
    case "actor": return record.name ?? (record.resolution === "redacted" ? "Restricted actor" : "Unknown actor");
    case "operation_run": return specs.get(record.operation_spec_id)?.name ?? "Operation run";
    case "binding": return `${record.direction} · ${record.role}`;
    case "binding_state_event": return `${record.state} · ${record.change_cause}`;
    case "operation_state_event": return `${record.state} · ${record.change_cause}`;
    case "supersession": return `Supersession · ${record.change_type}`;
    case "merge_input": return record.included ? "Included merge input" : "Excluded merge input";
    case "topology_retirement": return `${record.retired_entity_kind.split("_").join(" ")} · ${record.retirement_type}`;
    case "lifecycle_event": return record.event_type.split("_").join(" ");
    case "decision": return `Decision · ${record.status}`;
    case "decision_evidence": return `Evidence · ${record.ordinal + 1}`;
    case "decision_status_event": return `Decision · ${record.status}`;
    case "journal_entry": return `Journal entry · ${record.ordinal + 1}`;
    case "unknown": return `Unsupported · ${record.original_type}`;
  }
}

function nodeState(record: CanonicalRecord): string | null {
  switch (record.entity_kind) {
    case "artifact_version": return record.lifecycle_state;
    case "operation_run": return record.status;
    case "binding": return record.state.state;
    case "binding_state_event":
    case "operation_state_event": return record.state;
    case "lifecycle_event": return record.state;
    case "topology_retirement": return record.retirement_type;
    case "decision": return record.status;
    case "decision_status_event": return record.status;
    case "unknown": return "unsupported";
    default: return null;
  }
}

function nodeTimestamp(record: CanonicalRecord): string | null {
  switch (record.entity_kind) {
    case "artifact":
    case "artifact_version":
    case "operation_spec":
    case "operation_run":
    case "binding":
    case "supersession":
    case "merge_input":
    case "decision": return record.created_at;
    case "binding_state_event":
    case "operation_state_event": return record.effective_at ?? record.recorded_at;
    case "lifecycle_event":
    case "topology_retirement":
    case "decision_status_event":
    case "journal_entry": return record.effective_at;
    case "decision_evidence": return record.effective_at;
    default: return null;
  }
}

function canonicalNode(
  record: CanonicalRecord,
  model: CanonicalLineageReadModel,
  anchorKeys: ReadonlySet<string>,
): DAGNode {
  const recordRef = ref(record);
  const type = nodeType(record);
  const metadata = "metadata" in record && record.metadata && typeof record.metadata === "object"
    ? record.metadata as Record<string, unknown>
    : {};
  const digest = record.entity_kind === "artifact_version" ? record.content_identity.digest : undefined;
  const modelInstance = record.entity_kind === "artifact" ? record.model_instance : null;
  return {
    id: canonicalLineageRefKey(recordRef),
    path: canonicalLineageRefKey(recordRef),
    kind: record.entity_kind,
    type,
    node_type: type,
    record_type: type,
    name: nodeLabel(record, model),
    state: nodeState(record),
    created_at: nodeTimestamp(record) ?? undefined,
    digest,
    anchor: anchorKeys.has(canonicalLineageRefKey(recordRef)),
    metadata: {
      ...metadata,
      canonical_entity_kind: record.entity_kind,
      canonical_id: recordRef.id,
      canonical_record: record,
      ...(modelInstance ? {
        model_classification: modelInstance.classification,
        generic_artifact_id: modelInstance.generic_artifact_id,
      } : {}),
    },
    canonical_record: record,
  };
}

function boundaryNode(
  boundary: CanonicalLineageBoundary,
  modelInstanceTargetKeys: ReadonlySet<string>,
): DAGNode {
  const labels: Record<CanonicalLineageBoundary["reason"], string> = {
    outside_projection: "More lineage",
    not_found: "Missing record",
    unsupported: "Unsupported record",
  };
  const isModelInstanceTarget = modelInstanceTargetKeys.has(canonicalLineageRefKey(boundary));
  return {
    id: canonicalLineageRefKey(boundary),
    path: canonicalLineageRefKey(boundary),
    kind: "boundary",
    type: "lifecycle",
    node_type: "lifecycle",
    record_type: "lifecycle",
    name: labels[boundary.reason],
    state: boundary.reason,
    metadata: {
      boundary: true,
      canonical_entity_kind: boundary.entity_kind,
      canonical_id: boundary.id,
      boundary_reason: boundary.reason,
      boundary_detail: boundary.detail,
      expandable: boundary.reason === "outside_projection" && !isModelInstanceTarget,
      interactive: !isModelInstanceTarget,
      model_instance_target: isModelInstanceTarget,
    },
  };
}

/**
 * Adapt a canonical, lossless read model to the existing renderer shape.
 * The normalized model remains available on `MergedCanonicalLineagePages` and
 * each presentation node/edge retains its exact source record under `raw`.
 */
export function canonicalLineageToDag(
  model: CanonicalLineageReadModel,
  authoritativeRoots: readonly CanonicalLineageRecordRef[] = model.context.root_ids.map((id) => ({
    entity_kind: "artifact_version",
    id,
  })),
): DAGData {
  const records: CanonicalRecord[] = [];
  for (const collection of RECORD_COLLECTIONS) records.push(...model[collection] as CanonicalRecord[]);
  const anchorKeys = new Set(authoritativeRoots.map(canonicalLineageRefKey));
  const nodes = records.map((record) => canonicalNode(record, model, anchorKeys));
  const nodeIds = new Set(nodes.map((node) => node.id));
  const modelInstanceTargetKeys = new Set(model.artifacts.flatMap((artifact) => (
    artifact.model_instance.classification === "instance"
      && artifact.model_instance.generic_artifact_id !== null
      ? [canonicalLineageRefKey({
          entity_kind: "artifact",
          id: artifact.model_instance.generic_artifact_id,
        })]
      : []
  )));
  for (const boundary of model.context.boundary) {
    const key = canonicalLineageRefKey(boundary);
    if (!nodeIds.has(key)) {
      nodes.push(boundaryNode(boundary, modelInstanceTargetKeys));
      nodeIds.add(key);
    }
  }

  const edges: DAGEdge[] = [];
  const addEdge = (
    id: string,
    source: CanonicalLineageRecordRef,
    target: CanonicalLineageRecordRef,
    relationship: string,
    sourceRecord: unknown,
  ) => {
    const sourceId = canonicalLineageRefKey(source);
    const targetId = canonicalLineageRefKey(target);
    if (!nodeIds.has(sourceId) || !nodeIds.has(targetId) || sourceId === targetId) return;
    edges.push({
      id,
      parent_id: sourceId,
      child_id: targetId,
      relationship,
      metadata: {
        canonical_record: sourceRecord,
        ...(relationship === "instance_of" ? {
          context_only: true,
          propagates_invalidity: false,
        } : {}),
      },
      canonical_record: sourceRecord,
    });
  };
  const typed = (entity_kind: CanonicalLineageEntityKind, id: string): CanonicalLineageRecordRef => ({ entity_kind, id });

  for (const artifact of model.artifacts) {
    if (artifact.model_instance.classification === "instance" && artifact.model_instance.generic_artifact_id) {
      addEdge(
        `instance-of:${artifact.id}`,
        ref(artifact),
        typed("artifact", artifact.model_instance.generic_artifact_id),
        "instance_of",
        artifact.model_instance,
      );
    }
  }

  for (const version of model.artifact_versions) {
    addEdge(`artifact-version:${version.id}`, typed("artifact", version.artifact_id), ref(version), "has_version", version);
  }
  for (const run of model.operation_runs) {
    addEdge(`run-spec:${run.id}`, typed("operation_spec", run.operation_spec_id), ref(run), "specifies", run);
    addEdge(`run-tool:${run.id}`, typed("tool", run.tool_id), ref(run), "executed_with", run);
    addEdge(`run-actor:${run.id}`, typed("actor", run.actor_id), ref(run), "performed_by", run);
  }
  for (const binding of model.bindings) {
    if (binding.direction === "input") {
      addEdge(`binding-artifact:${binding.id}`, typed("artifact_version", binding.artifact_version_id), ref(binding), "binds_input", binding);
      addEdge(`binding-operation:${binding.id}`, ref(binding), typed("operation_run", binding.operation_id), "input_to", binding);
    } else {
      addEdge(`binding-operation:${binding.id}`, typed("operation_run", binding.operation_id), ref(binding), "binds_output", binding);
      addEdge(`binding-artifact:${binding.id}`, ref(binding), typed("artifact_version", binding.artifact_version_id), "outputs", binding);
    }
  }
  for (const event of model.binding_state_events) {
    addEdge(`binding-state:${event.id}`, ref(event), typed("binding", event.binding_id), "records_state", event);
    addEdge(`binding-state-actor:${event.id}`, typed("actor", event.actor_id), ref(event), "recorded_by", event);
  }
  for (const event of model.operation_state_events) {
    addEdge(`operation-state:${event.id}`, ref(event), typed("operation_run", event.operation_id), "records_state", event);
    addEdge(`operation-state-actor:${event.id}`, typed("actor", event.actor_id), ref(event), "recorded_by", event);
  }
  for (const supersession of model.supersessions) {
    addEdge(`supersession-old:${supersession.id}`, typed("artifact_version", supersession.old_artifact_version_id), ref(supersession), "superseded_by", supersession);
    addEdge(`supersession-new:${supersession.id}`, ref(supersession), typed("artifact_version", supersession.new_artifact_version_id), "supersedes", supersession);
  }
  for (const merge of model.merge_inputs) {
    addEdge(`merge-source:${merge.id}`, typed("operation_run", merge.source_operation_id), ref(merge), "merge_input", merge);
    addEdge(`merge-target:${merge.id}`, ref(merge), typed("operation_run", merge.merge_operation_id), merge.included ? "included_in_merge" : "excluded_from_merge", merge);
  }
  for (const retirement of model.topology_retirements) {
    const target = retirement.retired_entity_kind === "decision_evidence"
      ? typed("decision_evidence", `${retirement.retired_entity_id}:${retirement.retired_entity_ordinal}`)
      : typed(retirement.retired_entity_kind, retirement.retired_entity_id);
    addEdge(`topology-retirement:${retirement.id}`, ref(retirement), target, retirement.retirement_type, retirement);
  }
  for (const event of model.lifecycle_events) {
    if (event.caused_by_artifact_version_id) {
      addEdge(`lifecycle-cause:${event.id}`, typed("artifact_version", event.caused_by_artifact_version_id), ref(event), "caused", event);
    }
    addEdge(`lifecycle-target:${event.id}`, ref(event), typed("artifact_version", event.artifact_version_id), event.event_type, event);
    addEdge(`lifecycle-actor:${event.id}`, typed("actor", event.actor_id), ref(event), "recorded_by", event);
  }
  for (const decision of model.decisions) {
    addEdge(`decision-subject:${decision.id}`, typed("artifact_version", decision.artifact_version_id), ref(decision), "subject_of", decision);
    addEdge(`decision-actor:${decision.id}`, typed("actor", decision.created_by), ref(decision), "decided_by", decision);
  }
  for (const evidence of model.decision_evidence) {
    addEdge(`evidence-source:${evidenceIdentity(evidence)}`, typed("artifact_version", evidence.artifact_version_id), ref(evidence), "evidence", evidence);
    addEdge(`evidence-decision:${evidenceIdentity(evidence)}`, ref(evidence), typed("decision", evidence.decision_id), "supports", evidence);
  }
  for (const event of model.decision_status_events) {
    addEdge(`decision-status:${event.id}`, ref(event), typed("decision", event.decision_id), "records_status", event);
    addEdge(`decision-status-actor:${event.id}`, typed("actor", event.actor_id), ref(event), "recorded_by", event);
  }
  for (const entry of model.journal_entries) {
    addEdge(`journal-target:${entry.id}`, ref(entry), typed("artifact_version", entry.artifact_version_id), "documents", entry);
    addEdge(`journal-actor:${entry.id}`, typed("actor", entry.actor_id), ref(entry), "recorded_by", entry);
  }
  for (const relationship of model.unknown_relationships) {
    addEdge(`unknown:${relationship.id}`, relationship.source, relationship.target, `unsupported:${relationship.original_type}`, relationship);
  }

  return {
    nodes: nodes.sort((left, right) => left.id.localeCompare(right.id)),
    edges: edges.sort((left, right) => String(left.id).localeCompare(String(right.id))),
  };
}

/** Build the renderer input for either normalized records or server aggregates. */
export function canonicalProjectionToDag(projection: MergedCanonicalLineagePages): DAGData {
  if (!projection.buckets.length) return canonicalLineageToDag(projection.model, projection.roots);
  return {
    nodes: projection.buckets.map((bucket) => ({
      id: `bucket:${bucket.dimension}:${bucket.key}`,
      path: `bucket.${bucket.dimension}.${bucket.key}`,
      kind: `summary_${bucket.dimension}`,
      type: "artifact",
      node_type: "artifact",
      record_type: "artifact",
      name: bucket.label,
      anchor: false,
      metadata: {
        virtual: true,
        projection_bucket: bucket,
        group_by: bucket.dimension,
        group_key: bucket.key,
        member_count: bucket.count,
        representative_root: bucket.representative_root
          ? canonicalLineageRefKey(bucket.representative_root)
          : null,
      },
      canonical_bucket: bucket,
    })).sort((left, right) => left.id.localeCompare(right.id)),
    edges: [],
  };
}
